/**
 * What floats the browser preview besides the user: the panel closing on a
 * live session, and the agent driving a tab the panel is not showing.
 *
 * Ported from t3code's ChatView integration and automation open readiness
 * (MIT, pin `e3e7cc3f`), split out of the layer component to keep each
 * function under the complexity bar.
 *
 * @module useBrowserMiniPlayerTriggers
 */
import type { BrowserSessionSnapshot, ScopedThreadRef } from "@cmmd-forge/contracts";
import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import { useEffect, useRef } from "react";

import type { BrowserThreadActivity } from "~/browser/browserPointerStore";
import type { BrowserThreadState } from "~/browserStateStore";
import type { RightPanelSurface } from "~/rightPanelSurface";

import { shouldAutoShowBrowserMiniPlayer } from "./browserAutomationReadiness";
import {
  browserMiniPlayerSource,
  browserMiniPlayerSourceKey,
  selectBrowserMiniPlayerAutoShowSuppressed,
  useBrowserMiniPlayerStore,
} from "./browserMiniPlayerStore";

/**
 * Closing the panel on a live browser floats the tab it was showing. The
 * rendered surface can mirror a different tab than the session's active one,
 * so the tab the user was looking at wins; the active tab is only the
 * fallback for a placeholder surface with no tab of its own.
 */
export function useFloatBrowserOnPanelClose(input: {
  readonly threadRef: ScopedThreadRef;
  readonly session: BrowserSessionSnapshot | null;
  readonly renderedSurfaceId: string | null;
  readonly renderedTabId: string | null;
}) {
  const { threadRef, session, renderedSurfaceId, renderedTabId } = input;
  const previousSurfaceIdRef = useRef<string | null>(null);
  const previousTabIdRef = useRef<string | null>(null);
  useEffect(() => {
    const previousSurfaceId = previousSurfaceIdRef.current;
    previousSurfaceIdRef.current = renderedSurfaceId;
    const previousTabId = previousTabIdRef.current;
    previousTabIdRef.current = renderedTabId;
    if (renderedSurfaceId !== null) {
      useBrowserMiniPlayerStore.getState().clearAutoShowSuppression(threadRef);
      return;
    }
    if (previousSurfaceId === null) return;
    const tabId = previousTabId ?? session?.activeTabId ?? null;
    if (!session || !tabId) return;
    useBrowserMiniPlayerStore.getState().open(threadRef, browserMiniPlayerSource(tabId));
  }, [renderedSurfaceId, renderedTabId, session, threadRef]);
}

/**
 * The newest activity entry across the thread's tabs, with the tab that
 * produced it. Activity is recorded per event tab, so the tab the agent last
 * drove is not necessarily the session's active one; the auto-show decision
 * follows the activity, not the active tab.
 */
/**
 * What the layer derives from the thread and panel state each render: the
 * live session, and the tab/surface the panel was last mirroring.
 */
export function deriveBrowserMiniPlayerLayerState(input: {
  readonly threadState: BrowserThreadState | null;
  readonly visibleSurface: RightPanelSurface | null;
}): {
  readonly session: BrowserSessionSnapshot | null;
  readonly renderedPreviewSurfaceId: string | null;
  readonly renderedPreviewTabId: string | null;
  readonly activityTabId: string | null;
  readonly latestActivity: BrowserThreadActivity | null;
} {
  const { threadState, visibleSurface } = input;
  const session = threadState?.session ?? null;
  const renderedPreviewSurfaceId = visibleSurface?.kind === "preview" ? visibleSurface.id : null;
  const renderedPreviewTabId =
    visibleSurface?.kind === "preview" ? (visibleSurface.resourceId ?? null) : null;
  const latestActivityEntry = threadState
    ? latestBrowserActivity(threadState.lastActivityByTabId)
    : null;
  return {
    session,
    renderedPreviewSurfaceId,
    renderedPreviewTabId,
    activityTabId: latestActivityEntry?.tabId ?? null,
    latestActivity: latestActivityEntry?.activity ?? null,
  };
}

function latestBrowserActivity(
  lastActivityByTabId: Readonly<Record<string, BrowserThreadActivity>>,
): { readonly tabId: string; readonly activity: BrowserThreadActivity } | null {
  let latest: { readonly tabId: string; readonly activity: BrowserThreadActivity } | null = null;
  for (const [tabId, activity] of Object.entries(lastActivityByTabId)) {
    if (latest === null || activity.createdAt > latest.activity.createdAt) {
      latest = { tabId, activity };
    }
  }
  return latest;
}

/**
 * The agent driving a tab the panel is not showing floats it on its own
 * (upstream's automation auto-show). One float per activity entry; the
 * user's dismissal holds until the source changes.
 *
 * The activity feed outlives its session (`session.closed` keeps
 * `lastActivityByTabId`), so only a live session from the browser the
 * activity came from may float; a closed session's stale feed floats
 * nothing.
 */
export function useAutoShowBrowserMiniPlayer(input: {
  readonly threadRef: ScopedThreadRef;
  readonly session: BrowserSessionSnapshot | null;
  readonly activityTabId: string | null;
  readonly latestActivity: BrowserThreadActivity | null;
  readonly renderedInPanel: boolean;
}) {
  const { threadRef, session, activityTabId, latestActivity, renderedInPanel } = input;
  const lastAutoShownActivityRef = useRef<string | null>(null);
  useEffect(() => {
    if (!latestActivity || !activityTabId) return;
    if (lastAutoShownActivityRef.current === latestActivity.createdAt) return;
    if (!session || session.browserId !== latestActivity.browserId) return;
    const store = useBrowserMiniPlayerStore.getState();
    const currentlyFloating = store.byThreadKey[scopedThreadKey(threadRef)] ?? null;
    if (
      shouldAutoShowBrowserMiniPlayer({
        activity: latestActivity,
        renderedInPanel,
        floating: currentlyFloating !== null,
        suppressed: selectBrowserMiniPlayerAutoShowSuppressed(
          store.autoShowSuppressedByThreadKey,
          threadRef,
          browserMiniPlayerSourceKey({ kind: "browser", tabId: activityTabId }),
        ),
      })
    ) {
      lastAutoShownActivityRef.current = latestActivity.createdAt;
      store.open(threadRef, browserMiniPlayerSource(activityTabId));
    }
  }, [latestActivity, activityTabId, session, renderedInPanel, threadRef]);
}

/**
 * A closed session takes its floater with it. `session.closed` drops the
 * session but the floater entry would linger, so a replacement session would
 * inherit a stale tab and a blocked auto-show; the suppression record goes
 * too, because the browser the user dismissed on no longer exists.
 */
export function useClearBrowserMiniPlayerOnSessionClose(input: {
  readonly threadRef: ScopedThreadRef;
  readonly session: BrowserSessionSnapshot | null;
}) {
  const { threadRef, session } = input;
  const hadSessionRef = useRef(false);
  useEffect(() => {
    const hadSession = hadSessionRef.current;
    hadSessionRef.current = session !== null;
    if (hadSession && session === null) {
      useBrowserMiniPlayerStore.getState().removeThread(threadRef);
    }
  }, [session, threadRef]);
}
